import { stripHypothesisInstructionLeak } from '../core/agents/utils/working-hypothesis.js'
import type { TranscriptItem } from '../types/visual.js'
import { presentAttachmentTranscript } from './pdf-grounding.js'
import { generateTranscriptId } from './transcript-append.js'

export function isInjectedSayPrompt(text: string): boolean {
  return /^\s*\[SAY\]/i.test(text.trim())
}

/** A YOU chunk that is only the start of a model-only bind, not a visitor sentence. */
export function isLeakOpenerFragment(text: string): boolean {
  const value = text.replace(/\s+/g, ' ').trim().toLowerCase()
  if (!value || value.length > 48) return false
  const openers = [
    'named problem',
    'hypothesis correction',
    'do not read this instruction',
    'adopted bottleneck',
  ]
  return openers.some(opener => opener.startsWith(value))
}

export interface LiveTranscriptUpdate {
  text: string
  isUser: boolean
  isFinal: boolean
  turnId?: string
  groundingMetadata?: unknown
  preferredEntryId?: string
}

export interface LiveTranscriptReconciliation {
  transcript: TranscriptItem[]
  item: TranscriptItem | null
  changed: boolean
}

/**
 * Merge cumulative Gemini snapshots and non-overlapping deltas for one turn.
 * Bridge mints a unique display turnId per visible row; this only merges text
 * within that row.
 */
export function mergeLiveTranscriptSnapshot(previous: string, next: string): string {
  const normalizedNext = next.trim()
  if (!normalizedNext) return previous
  const normalizedPrevious = previous.trim()
  if (!normalizedPrevious) return normalizedNext
  if (normalizedNext === normalizedPrevious) return normalizedNext
  if (normalizedNext.startsWith(normalizedPrevious)) return normalizedNext
  if (normalizedPrevious.startsWith(normalizedNext)) return normalizedPrevious
  if (normalizedPrevious.endsWith(normalizedNext)) return normalizedPrevious
  if (normalizedNext.endsWith(normalizedPrevious) && normalizedPrevious.length >= 3) {
    return normalizedNext
  }
  if (normalizedNext.length >= 20 && normalizedPrevious.includes(normalizedNext)) {
    return normalizedPrevious
  }

  const previousStem = stripTrailingTranscriptPunctuation(normalizedPrevious)
  if (previousStem && isStemContinuation(normalizedNext, previousStem)) {
    return normalizedNext
  }

  const nextStem = stripTrailingTranscriptPunctuation(normalizedNext)
  if (nextStem && isStemContinuation(normalizedPrevious, nextStem)) {
    return normalizedPrevious.length >= normalizedNext.length
      ? normalizedPrevious
      : normalizedNext
  }

  const overlapped = mergeOverlappingTranscript(normalizedPrevious, normalizedNext)
  if (overlapped) return overlapped

  const separator =
    normalizedPrevious.endsWith(' ') || normalizedNext.startsWith(' ') ? '' : ' '
  return `${normalizedPrevious}${separator}${normalizedNext}`
}

function stripTrailingTranscriptPunctuation(text: string): string {
  return text.replace(/[\s.!?,;:]+$/u, '')
}

function isStemContinuation(text: string, stem: string): boolean {
  if (text === stem) return true
  return (
    text.startsWith(`${stem} `) ||
    text.startsWith(`${stem},`) ||
    text.startsWith(`${stem}.`) ||
    text.startsWith(`${stem}?`) ||
    text.startsWith(`${stem}!`) ||
    text.startsWith(`${stem};`) ||
    text.startsWith(`${stem}:`)
  )
}

function splitTranscriptWords(text: string): string[] {
  return text.split(/\s+/).filter(Boolean)
}

function transcriptWordsEqual(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false
  return left.every((word, index) => word === right[index])
}

function mergeOverlappingTranscript(previous: string, next: string): string | null {
  const previousWords = splitTranscriptWords(previous)
  const nextWords = splitTranscriptWords(next)
  if (previousWords.length === 0 || nextWords.length === 0) return null

  if (nextWords.length >= 3) {
    const marker = nextWords.slice(0, 3).join(' ')
    const first = previous.indexOf(marker)
    const second = first >= 0 ? previous.indexOf(marker, first + marker.length) : -1
    if (second >= 0) return next
  }

  const maxOverlap = Math.min(previousWords.length, nextWords.length)
  for (let overlap = maxOverlap; overlap >= 1; overlap -= 1) {
    if (overlap === 1 && (nextWords[0]?.length ?? 0) < 3) continue
    if (!transcriptWordsEqual(previousWords.slice(-overlap), nextWords.slice(0, overlap))) {
      continue
    }
    return [...previousWords.slice(0, -overlap), ...nextWords].join(' ')
  }

  return null
}

export function isGrowingUserAsrRevision(previous: string, incoming: string): boolean {
  const prev = previous.trim()
  const next = incoming.trim()
  if (!prev || !next) return false
  if (next.startsWith(prev) || prev.startsWith(next)) return true
  const prevStem = stripTrailingTranscriptPunctuation(prev)
  return Boolean(prevStem && isStemContinuation(next, prevStem))
}

export function looksLikeCompletedUtterance(text: string): boolean {
  return /[.!?…]["'»”’)]*$/u.test(text.trim())
}

export function isLateModelTranscriptFragment(closed: string, incoming: string): boolean {
  const prev = closed.trim()
  const next = incoming.trim()
  if (!prev || !next) return false
  if (splitTranscriptWords(next).length > 3) return false
  if (prev.endsWith(next)) return true
  const prevStem = stripTrailingTranscriptPunctuation(prev)
  const nextStem = stripTrailingTranscriptPunctuation(next)
  return Boolean(nextStem && prevStem.endsWith(nextStem))
}

/**
 * After generationComplete closes a model row, Gemini may re-send the same
 * cumulative snapshot. Treat exact / prefix / punct-normalized replays as
 * the closed utterance so we do not mint a duplicate F.B/c bubble.
 */
export function isClosedModelOutputReplay(closed: string, incoming: string): boolean {
  const prev = closed.trim()
  const next = incoming.trim()
  if (!prev || !next) return false
  if (prev === next) return true
  if (prev.startsWith(next)) return true
  if (
    stripTrailingTranscriptPunctuation(prev) ===
    stripTrailingTranscriptPunctuation(next)
  ) {
    return true
  }
  return isLateModelTranscriptFragment(prev, next)
}

export function isShortModelStub(text: string): boolean {
  return splitTranscriptWords(text).length <= 1
}

/**
 * Same visible utterance: growing/shrinking ASR prefix, or a late leftover
 * last-word fragment. Does not use merge-equality — that glued new sentences
 * onto the previous bubble.
 */
export function isSameTurnRevision(previous: string, incoming: string): boolean {
  if (isGrowingUserAsrRevision(previous, incoming)) return true
  return isLateModelTranscriptFragment(previous, incoming)
}

function optimisticAttachmentKey(item: TranscriptItem): string | null {
  const attachment = item.attachment
  if (!attachment) return null
  if (attachment.url) return `${attachment.mimeType ?? ''}|${attachment.url}`
  if (attachment.name) return `${attachment.mimeType ?? ''}|${attachment.name}`
  if (attachment.data) return `${attachment.mimeType ?? ''}|${attachment.data.slice(0, 48)}`
  return null
}

/**
 * Order key for a reply. It is the instant after the customer line, even when
 * the clock that built the reply is earlier. Merge sorts on this key.
 */
export function replyTimestampAfter(customerAt: Date, now = Date.now()): Date {
  return new Date(Math.max(now, customerAt.getTime() + 1))
}

function transcriptTimestampMs(item: TranscriptItem): number {
  const value = item.timestamp
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime()
  return Number.isFinite(ms) ? ms : 0
}

function insertTranscriptByTime(list: TranscriptItem[], item: TranscriptItem): TranscriptItem[] {
  const time = transcriptTimestampMs(item)
  const index = list.findIndex(existing => transcriptTimestampMs(existing) > time)
  if (index < 0) return [...list, item]
  return [...list.slice(0, index), item, ...list.slice(index)]
}

/**
 * Index in `list` of the nearest row that was above `item` on screen.
 * The server copy of a row has another id and a server-clock time, so a row
 * also matches on turn id or on the same role and text.
 */
function screenAnchorIndex(
  list: TranscriptItem[],
  item: TranscriptItem,
  previous: TranscriptItem[],
): number {
  const seenAt = previous.findIndex(row => row.id === item.id)
  for (let index = seenAt - 1; index >= 0; index -= 1) {
    const before = previous[index]
    if (!before) continue
    const byId = list.findIndex(row => row.id === before.id)
    if (byId >= 0) return byId
    if (before.turnId) {
      const byTurn = list.findIndex(row => row.turnId === before.turnId && row.role === before.role)
      if (byTurn >= 0) return byTurn
    }
    if (!before.text?.trim()) continue
    // Same text twice ("vet ikke"): the n-th on screen is the n-th in the list.
    const key = transcriptTextKey(before)
    const nth = previous.slice(0, index + 1).filter(row => row.text?.trim() && transcriptTextKey(row) === key).length
    let seen = 0
    for (let at = 0; at < list.length; at += 1) {
      const row = list[at]
      if (!row?.text?.trim() || transcriptTextKey(row) !== key) continue
      seen += 1
      if (seen === nth) return at
    }
  }
  return -1
}

/**
 * The visitor typed after the rows they saw. The browser clock and the server
 * clock differ, and the server stamps a line when it stores it, so time alone
 * can place the typed row above the line it follows (E2E 17.10 sess_649dc4ac).
 * Keep it below the row that was above it on screen.
 */
function insertTypedAfterSeen(
  list: TranscriptItem[],
  item: TranscriptItem,
  previous: TranscriptItem[],
): TranscriptItem[] {
  const byTime = insertTranscriptByTime(list, item)
  const anchor = screenAnchorIndex(list, item, previous)
  if (anchor < 0) return byTime
  const timeIndex = byTime.indexOf(item)
  if (timeIndex > anchor) return byTime
  return [...list.slice(0, anchor + 1), item, ...list.slice(anchor + 1)]
}

/** A local reply sits right under the row it answered, whatever the clocks say. */
function insertReplyAfterSeen(
  list: TranscriptItem[],
  item: TranscriptItem,
  previous: TranscriptItem[],
): TranscriptItem[] {
  const anchor = screenAnchorIndex(list, item, previous)
  if (anchor < 0) return insertTranscriptByTime(list, item)
  return [...list.slice(0, anchor + 1), item, ...list.slice(anchor + 1)]
}

function transcriptTextKey(item: TranscriptItem): string {
  return `${item.role}\n${item.text?.trim() ?? ''}`
}

/** One projected row covers one optimistic row with the same text. A second «vet ikke» stays. */
function textCoverBudget(rows: TranscriptItem[]): Map<string, number> {
  const budget = new Map<string, number>()
  for (const row of rows) {
    const text = row.text?.trim() ?? ''
    if (!text) continue
    const key = transcriptTextKey(row)
    budget.set(key, (budget.get(key) ?? 0) + 1)
  }
  return budget
}

function projectionCoversOptimistic(
  projected: TranscriptItem[],
  item: TranscriptItem,
  budget: Map<string, number>
): boolean {
  if (projected.some(existing => existing.id === item.id)) return true
  if (
    item.turnId &&
    projected.some(existing => existing.role === item.role && existing.turnId === item.turnId)
  ) {
    return true
  }
  const text = item.text?.trim() ?? ''
  if (!text) return false
  const key = transcriptTextKey(item)
  const left = budget.get(key) ?? 0
  if (left <= 0) return false
  budget.set(key, left - 1)
  return true
}

function isOptimisticGreetingRow(item: TranscriptItem): boolean {
  return (
    item.role === 'model' &&
    typeof item.id === 'string' &&
    item.id.startsWith('greeting-') &&
    Boolean(item.text?.trim()) &&
    item.attachment?.type !== 'intelligence_briefing'
  )
}

/**
 * Keep in-flight Live rows when a canonical projection arrives.
 * Also keep optimistic file/PDF and typed user rows — projections omit
 * uncommitted Live-owned turns, and hydrate was dropping the YOU bubble.
 * First-contact greeting-* model bubbles are client-injected and must survive
 * consent / init-session / Live hydrate until the server covers the same utterance.
 */
export function mergeLiveOptimisticTranscript(
  projected: TranscriptItem[],
  previous: TranscriptItem[]
): TranscriptItem[] {
  const liveRows = previous.filter(
    item => item.isLiveTranscript === true && Boolean(item.text?.trim())
  )
  const attachmentRows = previous.filter(item => {
    if (item.isLiveTranscript === true) return false
    return optimisticAttachmentKey(item) !== null
  })
  const typedUserRows = previous.filter(item => {
    if (item.role !== 'user') return false
    if (item.isLiveTranscript === true) return false
    if (optimisticAttachmentKey(item) !== null) return false
    return Boolean(item.text?.trim())
  })
  const greetingRows = previous.filter(
    item => isOptimisticGreetingRow(item) && item.isLiveTranscript !== true
  )
  const localModelRows = previous.filter(
    item =>
      item.role === 'model' &&
      item.isLiveTranscript !== true &&
      !isOptimisticGreetingRow(item) &&
      item.attachment?.type !== 'intelligence_briefing' &&
      Boolean(item.text?.trim())
  )
  if (
    liveRows.length === 0 &&
    attachmentRows.length === 0 &&
    typedUserRows.length === 0 &&
    greetingRows.length === 0 &&
    localModelRows.length === 0
  ) {
    return projected
  }

  let next = [...projected]
  const budget = textCoverBudget(projected)
  for (const live of liveRows) {
    let index = -1
    if (live.turnId) {
      index = next.findIndex(item => item.role === live.role && item.turnId === live.turnId)
    }
    if (index < 0) {
      index = next.findIndex(item => item.id === live.id)
    }
    // Projection already committed the same utterance under another id/turnId —
    // do not reinsert a second assistant/user bubble on hydrate.
    if (index < 0 && projectionCoversOptimistic(next, live, budget)) {
      continue
    }
    if (index >= 0) {
      const existing = next[index]
      if (!existing) continue
      const mergedStatus =
        live.status === 'streaming' && existing.isFinal !== true
          ? 'streaming'
          : existing.status ?? live.status
      next[index] = {
        ...existing,
        text: mergeLiveTranscriptSnapshot(existing.text, live.text),
        isLiveTranscript: true,
        ...(live.turnId || existing.turnId
          ? { turnId: live.turnId ?? existing.turnId }
          : {}),
        isFinal: existing.isFinal === true ? true : live.isFinal,
        ...(mergedStatus ? { status: mergedStatus } : {}),
        // A report card merged into a Live row is client-only. Keep it.
        ...(!existing.attachment && live.attachment ? { attachment: live.attachment } : {}),
      }
    } else {
      next = insertTypedAfterSeen(next, live, previous)
    }
  }

  const projectedAttachmentKeys = new Set(
    next
      .map(item => optimisticAttachmentKey(item))
      .filter((key): key is string => Boolean(key))
  )
  for (const item of attachmentRows) {
    if (next.some(existing => existing.id === item.id)) continue
    const key = optimisticAttachmentKey(item)
    if (key && projectedAttachmentKeys.has(key)) continue
    next = insertTranscriptByTime(next, item)
    if (key) projectedAttachmentKeys.add(key)
  }

  for (const item of typedUserRows) {
    if (projectionCoversOptimistic(next, item, budget)) continue
    next = insertTypedAfterSeen(next, item, previous)
  }

  for (const item of localModelRows) {
    if (projectionCoversOptimistic(next, item, budget)) continue
    next = insertReplyAfterSeen(next, item, previous)
  }

  for (const item of greetingRows) {
    if (projectionCoversOptimistic(next, item, budget)) continue
    next = insertTranscriptByTime(next, item)
  }

  return next
}

/**
 * Live event → identify turn → update that row → new turnId = new row.
 */
function dropInstructionRow(
  transcript: TranscriptItem[],
  targetIndex: number,
): LiveTranscriptReconciliation {
  if (targetIndex < 0) return { transcript, item: null, changed: false }
  const existing = transcript[targetIndex]
  const kept = stripHypothesisInstructionLeak(existing?.text ?? '').trim()
  const rawExisting = existing?.text.trim() ?? ''
  if (kept && kept === rawExisting) {
    return { transcript, item: existing ?? null, changed: false }
  }
  if (!kept) {
    return {
      transcript: transcript.filter((_, index) => index !== targetIndex),
      item: null,
      changed: true,
    }
  }
  const next = [...transcript]
  next[targetIndex] = { ...existing!, text: kept }
  return { transcript: next, item: next[targetIndex] ?? null, changed: true }
}

/** Same «#n» suffix canonicalTurnId strips in useLiveSessionBridge. */
function canonicalDisplayTurnId(turnId: string | undefined): string {
  if (!turnId) return ''
  return turnId.trim().replace(/#\d+$/, '')
}

function isOpenLiveModelRow(item: TranscriptItem | undefined): item is TranscriptItem {
  return Boolean(
    item &&
      item.role === 'model' &&
      item.isLiveTranscript === true &&
      item.isFinal !== true,
  )
}

function turnHasUserRow(transcript: TranscriptItem[], canonical: string): boolean {
  return transcript.some(
    item =>
      item.role === 'user' &&
      Boolean(item.turnId) &&
      canonicalDisplayTurnId(item.turnId) === canonical,
  )
}

/**
 * The late first YOU line of a turn goes in front of that turn's still-open
 * agent row. A turn that already has a YOU row appends. Finished, typed,
 * and other-turn rows stay where they are.
 */
function indexForNewUserRow(transcript: TranscriptItem[], update: LiveTranscriptUpdate): number {
  const incoming = update.turnId?.trim()
  if (!update.isUser || !incoming) return transcript.length
  const canonical = canonicalDisplayTurnId(incoming)
  if (!canonical || turnHasUserRow(transcript, canonical)) return transcript.length
  let index = transcript.length - 1
  if (!isOpenLiveModelRow(transcript[index])) return transcript.length
  if (canonicalDisplayTurnId(transcript[index]?.turnId) !== canonical) return transcript.length
  while (
    index > 0 &&
    isOpenLiveModelRow(transcript[index - 1]) &&
    canonicalDisplayTurnId(transcript[index - 1]?.turnId) === canonical
  ) {
    index -= 1
  }
  return index
}

export function reconcileLiveTranscriptUpdate(
  transcript: TranscriptItem[],
  update: LiveTranscriptUpdate
): LiveTranscriptReconciliation {
  const raw = update.text.trim()
  const incoming = stripHypothesisInstructionLeak(raw).trim()
  const role: TranscriptItem['role'] = update.isUser ? 'user' : 'model'
  const targetIndex = findTargetIndex(transcript, role, update)
  if (!incoming) {
    if (!raw || targetIndex < 0) return { transcript, item: null, changed: false }
    const existingText = transcript[targetIndex]?.text.trim() ?? ''
    // A partial YOU row that is only the start of the echoed bind.
    if (existingText && raw.startsWith(existingText) && existingText.length < raw.length) {
      return {
        transcript: transcript.filter((_, index) => index !== targetIndex),
        item: null,
        changed: true,
      }
    }
    return dropInstructionRow(transcript, targetIndex)
  }

  if (update.isUser && isPdfContentEcho(transcript, incoming)) {
    return { transcript, item: null, changed: false }
  }
  if (update.isUser && targetIndex < 0 && hasSameVisitorLine(transcript, incoming)) {
    const turnId = update.turnId?.trim()
    const newTurn =
      Boolean(turnId) && !transcript.some(item => item.role === 'user' && item.turnId === turnId)
    // An echo of the row already on screen has no new turn. A later utterance
    // that repeats the words (barge-in «Hallo?») is its own row.
    if (!newTurn) return { transcript, item: null, changed: false }
  }
  if (!update.isUser && targetIndex < 0 && hasSameAssistantLine(transcript, incoming)) {
    return { transcript, item: null, changed: false }
  }

  const visibleIncoming = update.isUser ? presentAttachmentTranscript(incoming) : incoming
  const existing = targetIndex >= 0 ? transcript[targetIndex] : undefined
  const text = stripHypothesisInstructionLeak(
    existing ? mergeLiveTranscriptSnapshot(existing.text, visibleIncoming) : visibleIncoming,
  ).trim()
  if (!text) return dropInstructionRow(transcript, targetIndex)
  const id =
    existing?.id ??
    (update.turnId ? `live-${role}-${update.turnId}` : generateTranscriptId(`live-${role}`))
  const nextItem: TranscriptItem = {
    ...(existing ?? {}),
    id,
    role,
    text,
    timestamp: existing?.timestamp ?? new Date(),
    isFinal: update.isFinal,
    status: update.isFinal ? 'complete' : 'streaming',
    isLiveTranscript: true,
    ...(update.turnId ? { turnId: update.turnId } : existing?.turnId ? { turnId: existing.turnId } : {}),
    ...(update.groundingMetadata !== undefined
      ? {
          groundingMetadata:
            update.groundingMetadata as TranscriptItem['groundingMetadata'],
        }
      : existing?.groundingMetadata
        ? { groundingMetadata: existing.groundingMetadata }
        : {}),
  }

  if (
    existing &&
    existing.text === nextItem.text &&
    existing.isFinal === nextItem.isFinal &&
    existing.status === nextItem.status &&
    existing.isLiveTranscript === nextItem.isLiveTranscript &&
    existing.turnId === nextItem.turnId &&
    existing.groundingMetadata === nextItem.groundingMetadata
  ) {
    return { transcript, item: existing, changed: false }
  }

  const next = [...transcript]
  if (targetIndex >= 0) {
    next[targetIndex] = nextItem
  } else {
    const at = indexForNewUserRow(transcript, update)
    if (at >= next.length) next.push(nextItem)
    else next.splice(at, 0, nextItem)
  }

  return { transcript: next, item: nextItem, changed: true }
}

function visitorLineKey(text: string): string {
  return text
    .replace(/\[PDF RULE:[^\]]*\]/gi, '')
    .replace(/\[PDF CONTENT:[\s\S]*?\]/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

/** A later Live echo of a line already on screen, even when a reply sits between them. */
function hasSameVisitorLine(transcript: TranscriptItem[], incoming: string): boolean {
  const key = visitorLineKey(incoming)
  if (!key) return false
  return transcript.some(item => item.role === 'user' && visitorLineKey(item.text) === key)
}

/** The close is already on screen. Do not paint it again above or below the last line. */
function hasSameAssistantLine(transcript: TranscriptItem[], incoming: string): boolean {
  const key = incoming.replace(/\s+/g, ' ').trim().toLowerCase()
  if (!key) return false
  return transcript.some(item => item.role === 'model' && item.text.replace(/\s+/g, ' ').trim().toLowerCase() === key)
}

/** The composer already showed the caption. Live's `[PDF CONTENT:]` echo is not a second visitor line. */
function isPdfContentEcho(transcript: TranscriptItem[], incoming: string): boolean {
  const marker = '[PDF CONTENT:'
  const at = incoming.indexOf(marker)
  if (at < 0) return false
  const caption = incoming.slice(0, at).trim()
  if (!caption) return false
  return transcript.some(item => item.role === 'user' && item.text.trim() === caption)
}

function findTargetIndex(
  transcript: TranscriptItem[],
  role: TranscriptItem['role'],
  update: LiveTranscriptUpdate
): number {
  if (update.preferredEntryId) {
    const preferredIndex = transcript.findIndex(
      item =>
        item.id === update.preferredEntryId &&
        item.role === role &&
        (item.isLiveTranscript === true || !item.text.trim())
    )
    if (preferredIndex >= 0) return preferredIndex
  }

  if (update.turnId) {
    for (let index = transcript.length - 1; index >= 0; index -= 1) {
      const item = transcript[index]
      if (item?.role === role && item.turnId === update.turnId) return index
    }
    if (update.isUser) {
      const incoming = stripHypothesisInstructionLeak(update.text).trim()
      for (let index = transcript.length - 1; index >= 0; index -= 1) {
        const item = transcript[index]
        if (item?.role !== 'user') continue
        if (item.isLiveTranscript === true) continue
        if (incoming && item.text.trim() === incoming) return index
      }
    }
    return -1
  }

  for (let index = transcript.length - 1; index >= 0; index -= 1) {
    const item = transcript[index]
    if (item?.role === role && item.isLiveTranscript === true && !item.isFinal) {
      return index
    }
  }

  return -1
}
